import React, { useEffect } from "react";
import { Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import {
  updateApprovalUser,
  addNewApprovalUser,
  getAllApprovalUser,
} from "src/app-redux/configuration/actions/configurationActions";
import { loader, selectAllApprovalUserList } from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddApprovalUser } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { Prompt } from "react-router-dom";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import { getUsersAction } from "src/app-redux/management/actions/managementActions";
import { selectUsers } from "src/app-redux/management/managementSlice";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
  left0: { paddingLeft: "0px!important" },
  right0: { paddingRight: "0px!important" },
  left20: { paddingLeft: "20px!important" },
  normal: { paddingLeft: "8px!important", paddingRight: "8px!important" },
}));

export interface IForm extends IAddApprovalUser {}

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch: any = useAppDispatch();

  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = React.useState<any>(null);

  const loading = useAppSelector(loader);
  const allApprovalUserList = useAppSelector(selectAllApprovalUserList);
  const allUsers = useAppSelector(selectUsers); // get Users from Store
  const validationSchema = object({
    userId: string().required("This field is required"),
  });
  const formik = useFormik<IAddApprovalUser>({
    enableReinitialize: true,
    initialValues: {
      id: "",
      userId: "",
      isActive: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updateApprovalUser(values));
      else res = await dispatch(addNewApprovalUser(values));
      if (res?.payload) {
        dispatch(getAllApprovalUser(false));
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
        setSelectedRecord(null);
      }
    },
  });
  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    setSelectedRecord(params);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("userId", params.userId);
    formik.setFieldValue("isActive", params.isActive);
  };
  useEffect(() => {
    dispatch(getUsersAction());
    dispatch(getAllApprovalUser(false));
  }, []);

  useEffect(() => {
    if (allApprovalUserList.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirty(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allApprovalUserList]);

  // Show prompt if the form is fully or partially filled and user wants to navigate away
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    // If prompt, when reloading or closing the tab
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        disabled={isEditMode}
                        label="User"
                        name="userId"
                        placeholder="Select User"
                        disableClearable={true}
                        optionsType={allUsers}
                        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                        required
                        value={allUsers.find((st: any) => st.id === formik.values.userId) || null}
                        onChange={(option: any) => {
                          formik.setFieldValue("userId", option.id);
                        }}
                        error={formik.touched.userId && Boolean(formik.errors.userId)}
                        helperText={formik.touched.userId && formik.errors.userId}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box mb={1} mt={1} className={"headerButtonsConfig"} style={{ float: "right" }}>
                      <CommonButton
                        // isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          setisEditMode(false);
                          setIsDirty(false);
                          formik.resetForm();
                          setSelectedRecord(null);
                        }}
                      >
                        Reset
                      </CommonButton>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        disable={
                          selectedRecord
                            ? formik.values.userId == selectedRecord.userId &&
                              formik.values.isActive == selectedRecord.isActive
                            : !formik.isValid ||
                              JSON.stringify(formik.values) === JSON.stringify(formik.initialValues)
                        }
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allApprovalUserList || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
